import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useProfile } from "@/lib/profile/useProfile";
import { useEvents } from "@/lib/events/useEvents";
import { getApp, APPS } from "@/lib/apps";

const toIsoLocal = (s: string) => (s ? new Date(s).toISOString() : "");

const NewEventInner = () => {
  const navigate = useNavigate();
  const { profile, loading: pLoading } = useProfile();
  const defaultApp = profile?.active_app ?? profile?.primary_app ?? APPS[0]?.id;

  const [app, setApp] = useState<string>(defaultApp ?? "");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationText, setLocationText] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [capacity, setCapacity] = useState<string>("");
  const [coverUrl, setCoverUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { createEvent } = useEvents({ app: app || null, scope: "mine" });
  const meta = useMemo(() => (app ? getApp(app as any) : null), [app]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    const res = await createEvent({
      app,
      title: title.trim(),
      description: description.trim() || null,
      location_text: locationText.trim() || null,
      location_lat: null,
      location_lng: null,
      starts_at: toIsoLocal(startsAt),
      ends_at: endsAt ? toIsoLocal(endsAt) : null,
      capacity: capacity ? parseInt(capacity, 10) : null,
      cover_image_url: coverUrl.trim() || null,
    });
    setSaving(false);
    if ("error" in res && res.error) {
      setErr(res.error);
      return;
    }
    if ("data" in res && res.data) {
      // Auto safety share: when the member's `auto_safety_share_default`
      // preference is on (default true) and they have an emergency contact,
      // automatically schedule pre-meet reminders so their emergency circle
      // is looped in. Failures here must NOT block event creation — surface
      // a soft warning only.
      const autoShare =
        (profile as unknown as { auto_safety_share_default?: boolean } | null)
          ?.auto_safety_share_default ?? true;
      const meetAt = toIsoLocal(startsAt);
      if (
        autoShare &&
        meetAt &&
        new Date(meetAt) > new Date() &&
        profile?.emergency_contact_phone
      ) {
        try {
          const { supabase } = await import("@/integrations/supabase/client");
          await supabase.rpc("fn_schedule_premeet_reminders", {
            _meet_at: meetAt,
            _meet_location: locationText.trim() || null,
            _emergency_contact_name: profile.emergency_contact_name ?? null,
            _emergency_contact_phone: profile.emergency_contact_phone ?? null,
          });
        } catch (err) {
          // Soft fail — event is already created.
          console.warn("[event] auto safety share failed", err);
        }
      }
      navigate(`/events/${res.data.id}`);
    }
  };

  if (pLoading) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12 text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }

  return (
    <main className={`mx-auto max-w-2xl px-6 py-10 ${meta?.themeClass ?? ""}`}>
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Events
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
          Host a new event
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            App
          </label>
          <select
            value={app}
            onChange={(e) => setApp(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            required
          >
            {APPS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} — {a.community}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Title
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            required
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            maxLength={2000}
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Starts at
            </label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
              className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ends at
            </label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Location
          </label>
          <input
            value={locationText}
            onChange={(e) => setLocationText(e.target.value)}
            placeholder="Venue name & address, or 'Online'"
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Capacity (optional)
            </label>
            <input
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Cover image URL
            </label>
            <input
              type="url"
              value={coverUrl}
              onChange={(e) => setCoverUrl(e.target.value)}
              placeholder="https://…"
              className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            />
          </div>
        </div>

        {err ? <p className="text-xs text-destructive">{err}</p> : null}

        <p className="text-xs text-muted-foreground">
          🛡️ Auto safety share is{" "}
          <span className="font-medium text-foreground">
            {((profile as unknown as { auto_safety_share_default?: boolean } | null)
              ?.auto_safety_share_default ?? true)
              ? "ON"
              : "OFF"}
          </span>
          {". "}
          When on and you have an emergency contact set, your circle is
          notified about this event automatically.{" "}
          <Link to="/account" className="underline">
            Manage in settings
          </Link>
          .
        </p>

        <div className="flex items-center justify-between gap-3 pt-2">
          <Link to="/events" className="text-xs text-muted-foreground hover:text-foreground">
            ← Back to events
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-foreground text-background px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? "Publishing…" : "Publish event"}
          </button>
        </div>
      </form>
    </main>
  );
};

const NewEvent = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <NewEventInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default NewEvent;
